import { useMemo, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import {
  TEMAS,
  formatarHsl,
  formatarRazao,
  razaoDoPar,
  tokensDoTema,
  type Tema,
} from '@/ui/estilos/contraste';
// O arquivo de verdade, como texto: toda cor desta aba sai dele, nos três blocos
// (:root, .dark e html.a11y-high-contrast). Nenhum valor é escrito à mão aqui.
import tokensCss from '@/ui/estilos/tokens.css?raw';

import { AccessibilityChecker } from './AccessibilityChecker';
import { ColorSwatch, type ParMedido } from './ColorSwatch';
import { GRUPOS_DA_PALETA, PANTONE } from './paleta';
import { usePrintMode } from './print/PrintMode';

/** Rótulo curto da aba de cada tema da auditoria. */
const ROTULO_DO_TEMA: Readonly<Record<string, string>> = {
  claro: 'Claro',
  escuro: 'Escuro',
  'alto contraste sobre o claro': 'Alto contraste (claro)',
  'alto contraste sobre o escuro': 'Alto contraste (escuro)',
};

/**
 * Os pares da auditoria em que o token entra, medidos no tema. Mostra os pares
 * em que ele é a FRENTE (texto, traço); se ele nunca é frente — um fundo como
 * `--primary` —, mostra aqueles em que ele é o fundo.
 */
function paresDoToken(
  token: string,
  tema: Tema,
  tokens: ReturnType<typeof tokensDoTema>,
): ParMedido[] {
  const medir = (papel: 'frente' | 'fundo'): ParMedido[] =>
    tema.pares
      .filter((par) => par[papel] === token)
      .flatMap((par) => {
        const r = razaoDoPar(par, tokens);
        return r === undefined
          ? []
          : [
              {
                descricao: par.descricao,
                com: papel === 'frente' ? par.fundo : par.frente,
                papel,
                razao: r,
                minimo: par.minimo,
              },
            ];
      });
  const comoFrente = medir('frente');
  return comoFrente.length > 0 ? comoFrente : medir('fundo');
}

/** Valor de aba e prefixo de id: sem espaço, que id de HTML e aria-controls não admitem. */
function idDoTema(tema: Tema): string {
  return tema.nome.replace(/\s+/g, '-');
}

function PaletaDoTema({ tema }: { tema: Tema }) {
  const tokens = useMemo(() => tokensDoTema(tokensCss, tema), [tema]);
  const slug = idDoTema(tema);

  return (
    <div className="space-y-8" data-tema={tema.nome}>
      {GRUPOS_DA_PALETA.map((grupo) => (
        <section key={grupo.id} aria-labelledby={`cores-${slug}-${grupo.id}`}>
          <h4 id={`cores-${slug}-${grupo.id}`} className="text-base font-semibold text-foreground">
            {grupo.titulo}
          </h4>
          <p className="text-sm text-muted-foreground mb-4">{grupo.descricao}</p>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {grupo.entradas.flatMap((entrada) => {
              const hsl = tokens.get(entrada.token);
              if (!hsl) return [];
              const pantone = PANTONE[entrada.token];
              return [
                <ColorSwatch
                  key={entrada.token}
                  token={entrada.token}
                  nome={pantone ? `${entrada.nome} — ${pantone}` : entrada.nome}
                  hsl={hsl}
                  uso={entrada.uso}
                  pares={paresDoToken(entrada.token, tema, tokens)}
                />,
              ];
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * A mesma paleta, no PAPEL: uma tabela compacta por tema (amostra pequena,
 * token, valor e os pares de contraste com a razão), em vez dos cartões da
 * tela. Os valores e as razões saem das mesmas funções — `tokensDoTema` e
 * `paresDoToken` —, nada escrito à mão. Cada linha leva a âncora do item do
 * catálogo, então o documento não parte uma linha entre duas folhas.
 */
function TabelaDoTemaNoPapel({ tema }: { tema: Tema }) {
  const tokens = useMemo(() => tokensDoTema(tokensCss, tema), [tema]);
  return (
    <table className="w-full text-[9px] leading-tight border-collapse" data-tema={tema.nome}>
      <thead>
        <tr className="border-b border-border text-left text-muted-foreground">
          <th scope="col" className="w-8 py-1 font-semibold">
            Cor
          </th>
          <th scope="col" className="py-1 pr-2 font-semibold">
            Token
          </th>
          <th scope="col" className="py-1 pr-2 font-semibold whitespace-nowrap">
            Valor (HSL)
          </th>
          <th scope="col" className="py-1 font-semibold">
            Pares de contraste (razão medida e mínimo)
          </th>
        </tr>
      </thead>
      {GRUPOS_DA_PALETA.map((grupo) => (
        <tbody key={grupo.id}>
          <tr>
            <th
              scope="colgroup"
              colSpan={4}
              className="pt-2 pb-0.5 text-left text-[10px] font-semibold text-foreground"
            >
              {grupo.titulo}
            </th>
          </tr>
          {grupo.entradas.flatMap((entrada) => {
            const hsl = tokens.get(entrada.token);
            if (!hsl) return [];
            const valor = formatarHsl(hsl);
            const pares = paresDoToken(entrada.token, tema, tokens);
            return [
              <tr
                key={entrada.token}
                className="border-t border-border align-top"
                data-search-id={`color-${entrada.token}`}
                data-token={entrada.token}
              >
                <td className="py-0.5 pr-2">
                  <span
                    className="block h-3 w-6 rounded-sm border border-border"
                    data-print-keep-bg=""
                    style={{ backgroundColor: `hsl(${valor})` }}
                  />
                </td>
                <td className="py-0.5 pr-2 font-mono text-foreground whitespace-nowrap">
                  --{entrada.token}
                </td>
                <td className="py-0.5 pr-2 font-mono text-muted-foreground whitespace-nowrap">
                  {valor}
                </td>
                <td className="py-0.5 text-muted-foreground">
                  {pares.length === 0
                    ? '—'
                    : pares
                        .map(
                          (par) =>
                            `${par.papel === 'frente' ? `sobre --${par.com}` : `--${par.com} por cima`} ${formatarRazao(par.razao)} (mín. ${formatarRazao(par.minimo)})`,
                        )
                        .join(' · ')}
                </td>
              </tr>,
            ];
          })}
        </tbody>
      ))}
    </table>
  );
}

export function CoresTab() {
  const printMode = usePrintMode();
  const [temaAtivo, setTemaAtivo] = useState(idDoTema(TEMAS[0]!));

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Paleta por tema</h3>
        <p className="text-sm text-muted-foreground">
          Valores lidos de <code>apps/web/src/ui/estilos/tokens.css</code> e razões de contraste
          medidas por <code>apps/web/src/ui/estilos/contraste.ts</code>, os mesmos pares e a mesma
          fórmula que <code>npm run auditar:contraste</code> confere antes de todo build. O alto
          contraste é uma classe a mais no <code>&lt;html&gt;</code>, por cima do claro ou do
          escuro: o token que ele não redefine vem do tema de baixo, por isso aparece duas vezes.
        </p>
      </section>

      {printMode ? (
        TEMAS.map((tema) => (
          <section key={tema.nome} className="space-y-1">
            <h3 className="text-sm font-semibold">Tema {ROTULO_DO_TEMA[tema.nome] ?? tema.nome}</h3>
            <TabelaDoTemaNoPapel tema={tema} />
          </section>
        ))
      ) : (
        <Tabs value={temaAtivo} onValueChange={setTemaAtivo}>
          <TabsList aria-label="Tema da paleta" className="h-auto flex-wrap justify-start">
            {TEMAS.map((tema) => (
              <TabsTrigger key={tema.nome} value={idDoTema(tema)}>
                {ROTULO_DO_TEMA[tema.nome] ?? tema.nome}
              </TabsTrigger>
            ))}
          </TabsList>
          {TEMAS.map((tema) => (
            <TabsContent key={tema.nome} value={idDoTema(tema)} className="mt-6">
              <PaletaDoTema tema={tema} />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <section>
        <h3 className="text-lg font-semibold mb-4">Verificador de contraste</h3>
        <AccessibilityChecker />
      </section>
    </div>
  );
}
